/**
 * WindDataLoader
 *
 * Fetches PNG-encoded wind tiles from CloudFront, decodes them into WebGL textures,
 * and manages a tile cache. Handles multi-resolution switching between GFS (global)
 * and HRRR (North America 3km).
 *
 * Supports tile pyramids: when metadata.maxZoom > 0, loads multiple tiles at the
 * appropriate zoom level and composites them into a single atlas texture.
 */

import { WIND_DATA_SOURCES } from '@/config/windConfig';
import type { GeoBounds, WindDataSource, WindMetadata } from '@/types/wind';

/** Cache entry for a loaded wind texture */
interface CachedTile {
  imageData: ImageData;
  timestamp: number;
}

/** Cache entry for metadata with TTL */
interface CachedMetadata {
  metadata: WindMetadata;
  fetchedAt: number;
}

/** Tile coordinate */
interface TileCoord {
  z: number;
  x: number;
  y: number;
}

/** Result of tiled loading — atlas ImageData + info about tile layout */
export interface TiledResult {
  imageData: ImageData;
  /** Geographic bounds of the atlas region */
  bounds: GeoBounds;
  /** Whether this is a multi-tile atlas (vs single z0 tile) */
  isTiled: boolean;
}

const MAX_CACHE_SIZE = 100; // More tiles now
/** Metadata cache TTL: 5 minutes (metadata changes when new forecasts are published) */
const METADATA_CACHE_TTL_MS = 5 * 60 * 1000;
/** Max atlas dimension — 2048x2048 is safe for all GPUs */
const MAX_ATLAS_SIZE = 2048;

export class WindDataLoader {
  private cache = new Map<string, CachedTile>();
  private metadataCache = new Map<string, CachedMetadata>();
  private loadingPromises = new Map<string, Promise<ImageData | null>>();
  /** Track which tile set is currently loaded to avoid redundant reloads */
  private currentTileKey = '';

  /**
   * Get the best data source for the current viewport
   * Prefers higher-resolution regional sources when the viewport is within their bounds
   */
  selectSource(bounds: GeoBounds, zoom: number): WindDataSource {
    // Try regional sources first (they're ordered by priority — highest res first)
    for (const source of WIND_DATA_SOURCES) {
      if (source.bounds && this.viewportOverlaps(bounds, source.bounds) && zoom >= source.minZoom) {
        return source;
      }
    }
    // Fall back to global source
    const global = WIND_DATA_SOURCES.find((s) => s.bounds === null);
    return global || WIND_DATA_SOURCES[WIND_DATA_SOURCES.length - 1];
  }

  /**
   * Check if the viewport significantly overlaps a region's bounds
   * Returns true if at least 30% of the viewport is within the region
   */
  private viewportOverlaps(viewport: GeoBounds, region: GeoBounds): boolean {
    const overlapWest = Math.max(viewport.west, region.west);
    const overlapEast = Math.min(viewport.east, region.east);
    const overlapSouth = Math.max(viewport.south, region.south);
    const overlapNorth = Math.min(viewport.north, region.north);

    if (overlapWest >= overlapEast || overlapSouth >= overlapNorth) return false;

    const overlapArea = (overlapEast - overlapWest) * (overlapNorth - overlapSouth);
    const viewportArea = (viewport.east - viewport.west) * (viewport.north - viewport.south);

    return viewportArea > 0 && overlapArea / viewportArea > 0.3;
  }

  /**
   * Fetch metadata for a data source (cached with TTL)
   * Metadata includes uMin/uMax/vMin/vMax which change with each forecast cycle,
   * so the cache expires after METADATA_CACHE_TTL_MS.
   */
  async fetchMetadata(source: WindDataSource): Promise<WindMetadata | null> {
    const cached = this.metadataCache.get(source.metadataUrl);
    if (cached && Date.now() - cached.fetchedAt < METADATA_CACHE_TTL_MS) {
      return cached.metadata;
    }

    try {
      const response = await fetch(source.metadataUrl);
      if (!response.ok) return null;
      const metadata: WindMetadata = await response.json();
      this.metadataCache.set(source.metadataUrl, { metadata, fetchedAt: Date.now() });
      return metadata;
    } catch (error) {
      console.warn(`WindDataLoader: Failed to fetch metadata from ${source.metadataUrl}`, error);
      // Return stale cache if available rather than nothing
      return cached?.metadata ?? null;
    }
  }

  /**
   * Load wind data — either a single z0 tile or a multi-tile atlas.
   * When metadata has maxZoom > 0, calculates visible tiles and composites them.
   */
  async loadWindData(
    source: WindDataSource,
    metadata: WindMetadata | null,
    viewportBounds: GeoBounds,
    mapZoom: number,
  ): Promise<TiledResult | null> {
    const maxDataZoom = metadata?.maxZoom ?? 0;

    if (maxDataZoom === 0) {
      // Legacy: single z0 tile
      const imageData = await this.loadWindImage(source);
      if (!imageData) return null;
      return {
        imageData,
        bounds: source.bounds ?? { west: -180, south: -90, east: 180, north: 90 },
        isTiled: false,
      };
    }

    // Multi-tile: calculate which tiles we need
    const dataBounds = metadata?.bounds ?? source.bounds ?? { west: -180, south: -90, east: 180, north: 90 };
    const tiles = this.calculateVisibleTiles(viewportBounds, mapZoom, maxDataZoom, dataBounds);

    if (tiles.length === 0) {
      // Viewport doesn't overlap data — fall back to z0
      const imageData = await this.loadWindImage(source);
      if (!imageData) return null;
      return { imageData, bounds: dataBounds, isTiled: false };
    }

    // Check if tile set changed
    const tileKey = `${source.tileUrl}:${tiles.map((t) => `${t.z}/${t.x}/${t.y}`).join(',')}`;
    if (tileKey === this.currentTileKey) {
      return null; // No change — caller keeps existing texture
    }

    // Load all tiles in parallel
    const tileSize = metadata?.tileSize ?? 512;
    const images = await this.loadTiles(source, tiles, tileSize);

    if (images.length === 0) {
      // All tiles failed — fall back to z0
      const imageData = await this.loadWindImage(source);
      if (!imageData) return null;
      return { imageData, bounds: dataBounds, isTiled: false };
    }

    // Composite into atlas
    const result = this.compositeToAtlas(tiles, images, tileSize, dataBounds);
    this.currentTileKey = tileKey;
    return result;
  }

  /**
   * Load a single z0 wind data image (backward compatible path)
   */
  async loadWindImage(source: WindDataSource): Promise<ImageData | null> {
    const cacheKey = `${source.tileUrl}/0/0/0`;

    // Return cached tile if available
    const cached = this.cache.get(cacheKey);
    if (cached) return cached.imageData;

    // Deduplicate in-flight requests
    const existing = this.loadingPromises.get(cacheKey);
    if (existing) return existing;

    const promise = this.fetchTileImage(source.tileUrl, 0, 0, 0, cacheKey);
    this.loadingPromises.set(cacheKey, promise);

    try {
      return await promise;
    } finally {
      this.loadingPromises.delete(cacheKey);
    }
  }

  /**
   * Calculate which tiles are visible in the current viewport.
   * Maps zoom → data zoom: z0 for zoom<3, z1 for 3-5, z2 for 5-7, z3 for 7+
   */
  calculateVisibleTiles(
    viewport: GeoBounds,
    mapZoom: number,
    maxDataZoom: number,
    dataBounds: GeoBounds,
  ): TileCoord[] {
    // Map zoom to data zoom level
    const dataZoom = Math.min(Math.floor(Math.max(0, mapZoom - 1) / 2), maxDataZoom);

    if (dataZoom === 0) return []; // z0 is loaded via the single-tile path

    const tilesPerSide = Math.pow(2, dataZoom);
    const lonRange = dataBounds.east - dataBounds.west;
    const latRange = dataBounds.north - dataBounds.south;

    // Convert viewport to tile indices
    const xMin = Math.floor(((viewport.west - dataBounds.west) / lonRange) * tilesPerSide);
    const xMax = Math.floor(((viewport.east - dataBounds.west) / lonRange) * tilesPerSide);
    const yMin = Math.floor(((dataBounds.north - viewport.north) / latRange) * tilesPerSide);
    const yMax = Math.floor(((dataBounds.north - viewport.south) / latRange) * tilesPerSide);

    // Expand range by 1 tile in each direction for seamless edges
    const tiles: TileCoord[] = [];
    for (let y = Math.max(0, yMin - 1); y <= Math.min(tilesPerSide - 1, yMax + 1); y++) {
      for (let x = xMin - 1; x <= xMax + 1; x++) {
        // Wrap x for global data crossing antimeridian
        const wrappedX = ((x % tilesPerSide) + tilesPerSide) % tilesPerSide;
        tiles.push({ z: dataZoom, x: wrappedX, y });
      }
    }

    return tiles;
  }

  /**
   * Load multiple tiles in parallel, returning ImageData for each (or null on failure).
   */
  private async loadTiles(
    source: WindDataSource,
    tiles: TileCoord[],
    tileSize: number,
  ): Promise<(ImageData | null)[]> {
    const promises = tiles.map((t) => {
      const cacheKey = `${source.tileUrl}/${t.z}/${t.x}/${t.y}`;
      const cached = this.cache.get(cacheKey);
      if (cached) return Promise.resolve(cached.imageData);

      const existing = this.loadingPromises.get(cacheKey);
      if (existing) return existing;

      const promise = this.fetchTileImage(source.tileUrl, t.z, t.x, t.y, cacheKey);
      this.loadingPromises.set(cacheKey, promise);
      return promise.finally(() => this.loadingPromises.delete(cacheKey));
    });

    return Promise.all(promises);
  }

  /**
   * Composite loaded tiles into a single atlas ImageData.
   * Returns atlas + the geographic bounds it covers.
   */
  private compositeToAtlas(
    tiles: TileCoord[],
    images: (ImageData | null)[],
    tileSize: number,
    dataBounds: GeoBounds,
  ): TiledResult | null {
    if (tiles.length === 0) return null;

    // Determine grid layout
    const xs = [...new Set(tiles.map((t) => t.x))].sort((a, b) => a - b);
    const ys = [...new Set(tiles.map((t) => t.y))].sort((a, b) => a - b);
    const cols = xs.length;
    const rows = ys.length;

    // Limit atlas size
    const atlasW = Math.min(cols * tileSize, MAX_ATLAS_SIZE);
    const atlasH = Math.min(rows * tileSize, MAX_ATLAS_SIZE);
    const cellW = Math.floor(atlasW / cols);
    const cellH = Math.floor(atlasH / rows);

    const canvas = new OffscreenCanvas(atlasW, atlasH);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    // Clear to mid-gray (128 = zero wind in normalized space)
    ctx.fillStyle = 'rgb(128, 128, 0)';
    ctx.fillRect(0, 0, atlasW, atlasH);

    // Draw each tile at its grid position
    for (let i = 0; i < tiles.length; i++) {
      const img = images[i];
      if (!img) continue;

      const col = xs.indexOf(tiles[i].x);
      const row = ys.indexOf(tiles[i].y);
      const dx = col * cellW;
      const dy = row * cellH;

      // ImageData → ImageBitmap for canvas drawing
      const tileBitmap = new OffscreenCanvas(img.width, img.height);
      const tileCtx = tileBitmap.getContext('2d');
      if (!tileCtx) continue;
      tileCtx.putImageData(img, 0, 0);
      ctx.drawImage(tileBitmap, dx, dy, cellW, cellH);
    }

    const atlasImageData = ctx.getImageData(0, 0, atlasW, atlasH);

    // Compute geographic bounds of the atlas
    const z = tiles[0].z;
    const tilesPerSide = Math.pow(2, z);
    const lonRange = dataBounds.east - dataBounds.west;
    const latRange = dataBounds.north - dataBounds.south;

    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const atlasBounds: GeoBounds = {
      west: dataBounds.west + (minX / tilesPerSide) * lonRange,
      east: dataBounds.west + ((maxX + 1) / tilesPerSide) * lonRange,
      north: dataBounds.north - (minY / tilesPerSide) * latRange,
      south: dataBounds.north - ((maxY + 1) / tilesPerSide) * latRange,
    };

    return {
      imageData: atlasImageData,
      bounds: atlasBounds,
      isTiled: true,
    };
  }

  /**
   * Fetch a single tile PNG
   */
  private async fetchTileImage(
    tileUrl: string,
    z: number,
    x: number,
    y: number,
    cacheKey: string,
  ): Promise<ImageData | null> {
    const url = `${tileUrl}/${z}/${x}/${y}.png`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.warn(`WindDataLoader: Failed to load ${url} (${response.status})`);
        return null;
      }

      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);

      // Draw to canvas to get pixel data
      const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      ctx.drawImage(bitmap, 0, 0);
      const imageData = ctx.getImageData(0, 0, bitmap.width, bitmap.height);

      // Cache it
      this.evictIfNeeded();
      this.cache.set(cacheKey, {
        imageData,
        timestamp: Date.now(),
      });

      return imageData;
    } catch (error) {
      console.warn(`WindDataLoader: Error loading wind tile ${url}`, error);
      return null;
    }
  }

  /**
   * Upload ImageData to a WebGL texture
   */
  createWindTexture(gl: WebGL2RenderingContext, imageData: ImageData): WebGLTexture | null {
    const texture = gl.createTexture();
    if (!texture) return null;

    gl.bindTexture(gl.TEXTURE_2D, texture);

    // Upload pixel data
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imageData);

    // LINEAR filtering = built-in bilinear interpolation between texels
    // This is the key to smooth wind field sampling
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    // Wrap horizontally (longitude wraps), clamp vertically (latitude doesn't)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    gl.bindTexture(gl.TEXTURE_2D, null);
    return texture;
  }

  /**
   * Check if visible tiles changed (call from viewport change handler)
   */
  hasVisibleTilesChanged(
    source: WindDataSource,
    metadata: WindMetadata | null,
    viewportBounds: GeoBounds,
    mapZoom: number,
  ): boolean {
    const maxDataZoom = metadata?.maxZoom ?? 0;
    if (maxDataZoom === 0) return false;

    const dataBounds = metadata?.bounds ?? source.bounds ?? { west: -180, south: -90, east: 180, north: 90 };
    const tiles = this.calculateVisibleTiles(viewportBounds, mapZoom, maxDataZoom, dataBounds);
    const tileKey = `${source.tileUrl}:${tiles.map((t) => `${t.z}/${t.x}/${t.y}`).join(',')}`;
    return tileKey !== this.currentTileKey;
  }

  /**
   * Evict oldest entries if cache is full
   */
  private evictIfNeeded(): void {
    if (this.cache.size < MAX_CACHE_SIZE) return;

    // Find and remove the oldest entry
    let oldestKey = '';
    let oldestTime = Number.POSITIVE_INFINITY;

    for (const [key, entry] of this.cache) {
      if (entry.timestamp < oldestTime) {
        oldestTime = entry.timestamp;
        oldestKey = key;
      }
    }

    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Clear all caches (called on layer removal)
   */
  dispose(): void {
    this.cache.clear();
    this.metadataCache.clear();
    this.loadingPromises.clear();
    this.currentTileKey = '';
  }
}
